// Render spoken sentences to a downloadable MP3.
//
// Why this is roundabout: the Web Speech API is play-only. `speechSynthesis`
// hands text to the browser's (usually the OS's) speech engine, which writes
// straight to the sound device — there is no AudioNode, no MediaStream, and no
// callback carrying samples, so JavaScript cannot read what it just spoke.
// The desktop Python build sidesteps this by rendering MP3s server-side with
// edge-tts; this app has no server.
//
// What is left is to capture the sound as it plays: ask for a screen-capture
// stream *with audio*, speak every sentence into it, record with
// MediaRecorder, then decode and re-encode the result as MP3 with lamejs.
//
// Consequences, all surfaced in the UI:
//   * Desktop Chrome / Edge only — Firefox and Safari do not offer audio with
//     getDisplayMedia, and neither does any mobile browser.
//   * The user must tick "Also share system audio" (or "Share tab audio") in
//     the picker. Speech engines on Windows and macOS play outside the tab's
//     own audio, so **system** audio is the reliable choice.
//   * Capture is real time: a 100-sentence unit takes as long to export as it
//     takes to read aloud.
//   * System audio means *all* of it — other apps' sound lands in the file
//     too, so the user should stay quiet while it runs.
import { isSupported as ttsSupported, speak, stop as ttsStop } from './tts';

export type Mp3ErrorCode =
  | 'unsupported' // browser cannot capture audio at all
  | 'denied' // user dismissed the picker
  | 'no_audio' // shared a screen/tab but without audio
  | 'silent' // captured audio, but the speech was not in it
  | 'aborted' // user cancelled, or stopped sharing mid-run
  | 'failed';

export class Mp3Error extends Error {
  code: Mp3ErrorCode;
  constructor(code: Mp3ErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'Mp3Error';
    this.code = code;
  }
}

export type Mp3Phase = 'speaking' | 'encoding';

export interface Mp3Progress {
  phase: Mp3Phase;
  /** 0-based sentence being spoken, or encoded-sample position. */
  done: number;
  total: number;
  line?: string;
}

export interface Mp3Options {
  rate?: number;
  voiceId?: string;
  /** Silence between sentences, in ms. */
  gapMs?: number;
  signal?: AbortSignal;
  onProgress?: (p: Mp3Progress) => void;
}

/** Whether this browser can capture audio at all — checked before offering the button. */
export function canExportMp3(): boolean {
  return (
    ttsSupported() &&
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  );
}

const LEAD_IN_MS = 400; // recorder settling time before the first word
const TAIL_MS = 600; // let the last word finish before the recorder stops
const SILENCE_PEAK = 0.002; // below this the capture held no speech

/**
 * Speak every line while recording the system audio, and return the result as
 * an MP3. Rejects with an {@link Mp3Error} the caller can translate.
 */
export async function exportSpeechToMp3(lines: string[], opts: Mp3Options = {}): Promise<Blob> {
  if (!canExportMp3()) throw new Mp3Error('unsupported');
  const texts = lines.map((l) => l.trim()).filter(Boolean);
  if (!texts.length) throw new Mp3Error('failed', 'nothing to speak');

  const stream = await requestAudioCapture();
  const audioTracks = stream.getAudioTracks();
  if (!audioTracks.length) {
    stopStream(stream);
    throw new Mp3Error('no_audio');
  }

  // The picker forces a video track on us; we record audio only, but the video
  // track has to stay live or the browser tears the whole capture down.
  let captured: Blob;
  try {
    captured = await recordWhile(stream, audioTracks, () => speakAll(texts, opts));
  } finally {
    stopStream(stream);
    ttsStop();
  }

  const samples = await decodeToMono(captured);
  if (peak(samples.data) < SILENCE_PEAK) throw new Mp3Error('silent');
  return encodeMp3(samples, opts);
}

async function requestAudioCapture(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getDisplayMedia({
      // getDisplayMedia refuses an audio-only request, so video must be asked
      // for even though it is discarded.
      video: true,
      // Speech is the only thing we want recorded — the voice "cleanup"
      // filters would duck and colour it.
      audio: {
        autoGainControl: false,
        echoCancellation: false,
        noiseSuppression: false,
      },
    });
  } catch (e) {
    const name = (e as { name?: string })?.name;
    if (name === 'NotAllowedError' || name === 'AbortError') throw new Mp3Error('denied');
    throw new Mp3Error('failed', String(e));
  }
}

function stopStream(stream: MediaStream): void {
  for (const track of stream.getTracks()) track.stop();
}

/** MediaRecorder container support differs by browser; take the first that works. */
function pickMimeType(): string | undefined {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t));
}

/** Run `body` with the recorder running, and resolve with what was captured. */
async function recordWhile(
  stream: MediaStream,
  audioTracks: MediaStreamTrack[],
  body: () => Promise<void>,
): Promise<Blob> {
  const mimeType = pickMimeType();
  const recorder = new MediaRecorder(new MediaStream(audioTracks), {
    ...(mimeType ? { mimeType } : {}),
    audioBitsPerSecond: 128_000,
  });

  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data);
  };
  const finished = new Promise<void>((resolve, reject) => {
    recorder.onstop = () => resolve();
    recorder.onerror = () => reject(new Mp3Error('failed', 'recording failed'));
  });

  // Chrome's own "Stop sharing" bar ends the capture behind our back.
  const sharingEnded = new Promise<never>((_, reject) => {
    for (const track of stream.getTracks()) {
      track.addEventListener('ended', () => reject(new Mp3Error('aborted')), { once: true });
    }
  });
  // Losing the race leaves this promise pending; a rejection afterwards would
  // otherwise surface as an unhandled rejection.
  sharingEnded.catch(() => {});

  recorder.start();
  try {
    await Promise.race([body(), sharingEnded]);
  } finally {
    if (recorder.state !== 'inactive') recorder.stop();
  }
  await finished;

  return new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' });
}

/** Speak each line in turn, padded so the recorder clips neither end. */
async function speakAll(texts: string[], opts: Mp3Options): Promise<void> {
  const gap = opts.gapMs ?? 500;
  await wait(LEAD_IN_MS, opts.signal);
  for (let i = 0; i < texts.length; i++) {
    throwIfAborted(opts.signal);
    opts.onProgress?.({ phase: 'speaking', done: i, total: texts.length, line: texts[i] });
    await speakOnce(texts[i], opts);
    if (i < texts.length - 1) await wait(gap, opts.signal);
  }
  opts.onProgress?.({ phase: 'speaking', done: texts.length, total: texts.length });
  await wait(TAIL_MS, opts.signal);
}

function speakOnce(text: string, opts: Mp3Options): Promise<void> {
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      ttsStop();
      reject(new Mp3Error('aborted'));
    };
    opts.signal?.addEventListener('abort', onAbort, { once: true });
    const settle = (fn: () => void) => {
      opts.signal?.removeEventListener('abort', onAbort);
      fn();
    };
    speak(text, {
      rate: opts.rate,
      voiceId: opts.voiceId,
      onDone: () => settle(resolve),
      // A stop we did not ask for still ends this utterance; carry on rather
      // than losing the whole recording over one sentence.
      onStopped: () => settle(resolve),
      onError: () => settle(resolve),
    });
  });
}

interface MonoAudio {
  data: Float32Array;
  sampleRate: number;
}

/** lamejs only accepts the MPEG sample rates; anything else is resampled. */
const MP3_RATES = [8000, 11025, 12000, 16000, 22050, 24000, 32000, 44100, 48000];

async function decodeToMono(blob: Blob): Promise<MonoAudio> {
  const buffer = await blob.arrayBuffer();
  const ctx = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(buffer);
  } catch (e) {
    throw new Mp3Error('failed', `could not decode the recording: ${e}`);
  } finally {
    void ctx.close();
  }

  if (!MP3_RATES.includes(decoded.sampleRate)) {
    decoded = await resample(decoded, 44100);
  }
  return { data: downmix(decoded), sampleRate: decoded.sampleRate };
}

async function resample(buffer: AudioBuffer, sampleRate: number): Promise<AudioBuffer> {
  const frames = Math.max(1, Math.ceil(buffer.duration * sampleRate));
  const ctx = new OfflineAudioContext(buffer.numberOfChannels, frames, sampleRate);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(ctx.destination);
  src.start();
  return ctx.startRendering();
}

/** Speech is mono; averaging the channels halves the file for no quality cost. */
function downmix(buffer: AudioBuffer): Float32Array {
  if (buffer.numberOfChannels === 1) return buffer.getChannelData(0);
  const out = new Float32Array(buffer.length);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const channel = buffer.getChannelData(c);
    for (let i = 0; i < out.length; i++) out[i] += channel[i];
  }
  for (let i = 0; i < out.length; i++) out[i] /= buffer.numberOfChannels;
  return out;
}

function peak(data: Float32Array): number {
  let max = 0;
  for (let i = 0; i < data.length; i++) {
    const v = Math.abs(data[i]);
    if (v > max) max = v;
  }
  return max;
}

const FRAME = 1152; // one MPEG granule pair — what encodeBuffer expects
const YIELD_EVERY = 200; // frames between UI updates

async function encodeMp3({ data, sampleRate }: MonoAudio, opts: Mp3Options): Promise<Blob> {
  // The encoder is only needed once a recording exists, so it is fetched here
  // rather than shipped to every visitor in the initial bundle.
  const { Mp3Encoder } = await import('@breezystack/lamejs');
  const encoder = new Mp3Encoder(1, sampleRate, 128);
  const pcm = toInt16(data);
  const parts: Uint8Array[] = [];
  const frames = Math.ceil(pcm.length / FRAME);

  for (let f = 0; f < frames; f++) {
    throwIfAborted(opts.signal);
    // encodeBuffer hands back a view onto a buffer it reuses — copy it.
    const chunk = encoder.encodeBuffer(pcm.subarray(f * FRAME, (f + 1) * FRAME));
    if (chunk.length) parts.push(new Uint8Array(chunk));
    if (f % YIELD_EVERY === 0) {
      opts.onProgress?.({ phase: 'encoding', done: f, total: frames });
      await yieldToUi(); // lamejs is pure JS and would otherwise freeze the page
    }
  }
  const tail = encoder.flush();
  if (tail.length) parts.push(new Uint8Array(tail));
  opts.onProgress?.({ phase: 'encoding', done: frames, total: frames });

  return new Blob(parts as BlobPart[], { type: 'audio/mpeg' });
}

function toInt16(data: Float32Array): Int16Array {
  const out = new Int16Array(data.length);
  for (let i = 0; i < data.length; i++) {
    const v = Math.max(-1, Math.min(1, data[i]));
    out[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
  }
  return out;
}

function yieldToUi(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new Mp3Error('aborted'));
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(new Mp3Error('aborted'));
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new Mp3Error('aborted');
}

/** Save a blob under `name`, the same way the JSON export does. */
export function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
